// eslint-disable-next-line import/no-cycle
import { useRuntimeConfig } from '#imports';
import { merge } from 'lodash-es';
import Url from 'url';
import type { MappedResponseType } from 'ofetch/dist';
import type { RedeemedCouponResponse } from '~/stores/coupon';
import type { Cart } from '~/lib/types/cartdata';

type ErrorItem = {
  count?: number;
  error: string | string[];
  value?: string;
};

type QuantityError = {
  count: number;
  error: string;
  value: number;
};

export type BackendError<
  T extends string | number | symbol = string,
  V = Record<string, string | any>,
> = Record<T, V[]>;

export type BackendResponse = {
  order?: Cart;
  errors?: BackendError<'articleNumber' | 'stock' | 'quantity' | string, ErrorItem | QuantityError>;
};

export type BackendCouponInfoResponse = {
  couponVoucher: RedeemedCouponResponse;
  errors?: BackendError<'redeemedCoupon', 'no_valid_articles' | 'invalid' | string>;
};

const backendApiUrl = '/api/shop';

// See: https://wiki.i22.de/display/TDSMARTHOME/Shop+Api+Calls
export const endpoints = {
  order: `${backendApiUrl}/order`,
  confirmation: `${backendApiUrl}/confirmation`,
  coupon: `${backendApiUrl}/coupon`,
  getCoupon: (value: string) => `${backendApiUrl}/coupon?redeemed_coupon=${value}`,
  items: `${backendApiUrl}/items`,
  availabilitySubscriptions: `${backendApiUrl}/availability_subscriptions`,
  paypal_express: `${backendApiUrl}/paypal_express`,
  revertOrderToCart: `${backendApiUrl}/revert_order_to_cart`,
  splitJointCart: `${backendApiUrl}/split_joint_cart`,

  magentaCouponsConfirm: `${backendApiUrl}/magenta_coupons/confirm`,
  magentaCoupons: `${backendApiUrl}/magenta_coupons/`,

  dcbVouchers: `${backendApiUrl}/dcb_vouchers/`,
  dcbVouchersDOI: `${backendApiUrl}/dcb_vouchers/confirm`,
  shareCart: `${backendApiUrl}/share_carts/confirm`,
};

function addAfterPaymentUrls(
  orderParams: Record<string, unknown> | undefined
): Record<string, unknown> {
  const config = useRuntimeConfig();
  const { baseUrl } = config.public;
  // !!! On production the urls are hardcoded in BE !!!

  // We explicitly list the callbacks that the ShopApi Backend will pass to Brodos
  // and which in turn will be called by the browser after the payment process
  // has reached certain checkpoints
  const afterPaymentUrls = {
    brodosSuccessUrl: new URL('/api/brodos-response/success', baseUrl).href,
    brodosFailureUrl: new URL('/api/brodos-response/failure', baseUrl).href,
    novalnetSuccessUrl: new URL('/api/brodos-response/success', baseUrl).href,
    novalnetFailureUrl: new URL('/api/brodos-response/failure', baseUrl).href,
    confirmationUrl: new URL('/shop/bestaetigung', baseUrl).href,
    telekomPaymentInformationUrl: new URL('/shop/telekom-bezahlung-information', baseUrl).href,
    notifyUrl: new URL('/api/computop-response/notify', baseUrl).href,
    dimocoNotifyUrl: new URL('/api/dimoco-response/notify', baseUrl).href,
    successUrlTelekom: new URL('/shop/telekom-zahlungsverifizierung', baseUrl).href,
    successUrlBrodos: new URL('/shop/brodos-zahlungsverifizierung', baseUrl).href,
    successUrl: new URL('/shop/zahlungsverifizierung', baseUrl).href,
    failureUrl: new URL('/shop/fehler', baseUrl).href,
  };

  return merge({}, orderParams, afterPaymentUrls);
}

const apiCalls = {
  availabilitySubscriptions(subscription: any): Promise<MappedResponseType<any>> {
    return $shopApi(endpoints.availabilitySubscriptions, {
      method: 'POST',
      body: { subscription },
    });
  },
  submitOrder(orderParams: Record<string, unknown>): Promise<MappedResponseType<'json', any>> {
    const paramsWithAfterPaymentUrls = addAfterPaymentUrls(orderParams);
    return $shopApi(endpoints.order, {
      method: 'POST',
      body: { order: paramsWithAfterPaymentUrls },
    });
  },
  submitPaypalExpress(
    orderParams: Record<string, unknown>
  ): Promise<MappedResponseType<'json', any>> {
    const paramsWithAfterPaymentUrls = addAfterPaymentUrls(orderParams);
    return $shopApi(endpoints.paypal_express, {
      method: 'PATCH',
      body: { order: paramsWithAfterPaymentUrls },
    });
  },
  confirmOrder(): Promise<MappedResponseType<'json', any>> {
    return $shopApi(endpoints.confirmation);
  },
  deleteOrder(): Promise<MappedResponseType<'json', any>> {
    return $shopApi(endpoints.order, { method: 'DELETE' });
  },
  getCurrentCart(showLoadingSpinner = true): Promise<MappedResponseType<'json', BackendResponse>> {
    return $shopApi(endpoints.order, {
      // @ts-ignore
      loadingSpinner: showLoadingSpinner,
    });
  },
  items(
    items: { quantity?: number; articleNumber: string }[]
  ): Promise<MappedResponseType<'json', BackendResponse>> {
    return $shopApi(endpoints.items, { method: 'POST', body: { items } });
  },
  addCouponToCart(redeemedCoupon: string): Promise<MappedResponseType<'json', BackendResponse>> {
    // !This does return the normal Cart Response and NOT the CouponResponse!
    return $shopApi(endpoints.coupon, { method: 'PATCH', body: { order: { redeemedCoupon } } });
  },
  deleteCoupon(): Promise<MappedResponseType<'json', any>> {
    return $shopApi(endpoints.coupon, { method: 'DELETE' });
  },
  getCouponInfo(
    couponValue: string
  ): Promise<MappedResponseType<'json', BackendCouponInfoResponse>> {
    return $shopApi(endpoints.getCoupon(couponValue));
  },
  updateCustomerData(order: Partial<Cart>): Promise<MappedResponseType<'json', any>> {
    return $shopApi(endpoints.order, { method: 'PATCH', body: { order } });
  },
  revertOrderToCart(): Promise<MappedResponseType<'json', any>> {
    return $shopApi(endpoints.revertOrderToCart, {});
  },
  splitJointCart(): Promise<Record<'order', Cart>> {
    return $shopApi(endpoints.splitJointCart, {});
  },
  dcbVouchers(
    email: string,
    phoneNumber: string,
    reminder?: boolean,
    couponCode?: string,
    wtmc?: string | undefined
  ): Promise<MappedResponseType<'json', { status?: 'verified'; error?: string }>> {
    const config = useRuntimeConfig();
    const { baseUrl } = config.public;
    return $shopApi(endpoints.dcbVouchers, {
      method: 'POST',
      body: {
        email,
        phoneNumber,
        wtmc,
        reminder,
        couponCode,
        frontend_url: `${baseUrl}/handyrechnung-aktion`,
      },
    });
  },
  dcbVouchersDOI(
    doubleOptinToken: string
  ): Promise<MappedResponseType<'json', { status?: 'verified'; error?: string }>> {
    return $shopApi(endpoints.dcbVouchersDOI, {
      method: 'POST',
      body: { double_optin_token: doubleOptinToken },
    });
  },
  shareCart(
    email: string
  ): Promise<MappedResponseType<'json', { status: 'Cart send.'; error?: any }>> {
    const config = useRuntimeConfig();
    const { baseUrl } = config.public;
    return $shopApi(endpoints.shareCart, {
      method: 'POST',
      body: { email, shop_url: `${baseUrl}/shop/warenkorb` },
    });
  },
};

export default apiCalls;
